import * as React from 'react';
import { AgencyConnectureSetup } from '../Components/AgencyConnectureSetup';
import { AgencySunFireSetup } from '../Components/AgencySunFireSetup';
import { AgencyHealthSherpaMedicareSetup } from '../Components/AgencyHealthSherpaMedicareSetup';
import { AgencyPersonalTokenRepository, AgencyRepository } from '../../MedicareProAPI';
import type { Agency } from '../../MedicareProAPI/Entities/Agency/Agency';
import type { AgencyConnecture } from '../../MedicareProAPI/Entities/Agency/AgencyConnecture';
import type { GlobSunfireOrg } from '../../MedicareProAPI/Entities/Glob/GlobSunfireOrg';

interface AgencyEnrollmentSetupPanelProps {
  userAccessToken: string;
  userId: number;
  agencyId: number | null;
}

/**
 * Panel for managing enrollment setups for agencies
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2025-10-30
 * @lastUpdated
 * @version 1.1.0
 */
export const AgencyEnrollmentSetupPanel = ({
  userAccessToken,
  userId,
  agencyId,
}: AgencyEnrollmentSetupPanelProps): React.JSX.Element => {
  const [loading, setLoading] = React.useState(true);
  const [agency, setAgency] = React.useState<Agency | null>(null);
  const [connecture, setConnecture] = React.useState<AgencyConnecture | null>(null);
  const [sunfire, setSunfire] = React.useState<GlobSunfireOrg | null>(null);
  const [healthSherpaAgencyId, setHealthSherpaAgencyId] = React.useState<number | null>(null);

  const agencyRepository = React.useMemo(() => {
    const r = new AgencyRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const agencyPersonalTokenRepository = React.useMemo(() => {
    const r = new AgencyPersonalTokenRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  /**
   * Load agency integration state (SunFire, Connecture) and HS agency token.
   *
   * @dependency agencyRepository, agencyPersonalTokenRepository, agencyId, userId
   */
  React.useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const agencyResp = await agencyRepository.getAgency(
          userId,
          agencyId,
          { include: ['sunfire', 'connecture'] },
          true
        );
        if (!mounted) return;

        setAgency(agencyResp);
        setConnecture(agencyResp.getConnecture() ?? null);
        setSunfire(agencyResp.getSunfireOrg() ?? null);

        // HealthSherpa agency token present?
        const hs = await agencyPersonalTokenRepository.getAgencyPersonalToken(
          agencyId,
          'healthsherpamedicare'
        );
        if (!mounted) return;
        if (hs.exists && hs.id) setHealthSherpaAgencyId(hs.id);
      } catch (e) {
        console.error(e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [agencyRepository, agencyPersonalTokenRepository, agencyId, userId]);

  return (
    <>
      {!loading && (
        <>
          <AgencyConnectureSetup
            userAccessToken={userAccessToken}
            userId={userId}
            agencyId={agencyId}
            thisAgency={agency}
            thisConnecture={connecture}
          />

          <AgencyHealthSherpaMedicareSetup
            userAccessToken={userAccessToken}
            userId={userId}
            agencyId={agencyId}
            thisAgency={agency}
            thisHSFM={healthSherpaAgencyId}
          />

          <AgencySunFireSetup
            userAccessToken={userAccessToken}
            userId={userId}
            agencyId={agencyId}
            thisAgency={agency}
            thisSunFire={sunfire}
          />
        </>
      )}
    </>
  );
};
